import {
  ref,
  nextTick,
  computed,
  watchEffect,
  Ref,
} from "@vue/composition-api";
import {
  GraphicInfo,
  squareMeterToMu,
  clonePath,
  latLngToLngLat,
  calculateCenter,
  toClosedPath,
  latLngListToLngLatList,
  getAreaByCoords,
  getLengthByCoords,
} from "@tuliu/base-map";
import { LatLngTuple } from "@tuliu/leaflet";
import { useOnEditorSingleLand } from "./useAPPCallJS";
import { getPolygonImgBase64 } from "@/utils/screenshot";
import { JSCallAPP } from "@/utils/interactive";
import { Toast } from "vant";
import { DRAW_MODEL } from "@/constants/drawModel";
import { loading } from "@/utils/loading";
import { removeDuplicateItem } from "@/utils/geo";
import { useDrawStore } from "@/store/draw";
import { storeToRefs } from "pinia";
import { eventHub } from "@/utils/eventHub";

/**
 * 地图绘制相关逻辑
 * @param mapRef
 * @returns
 */
export const useDrawing = () => {
  const store = useDrawStore();

  const {
    graphicInfoList: sGraphicInfoList,
    initGraphicInfoList: sInitGraphicInfoList,
    snapshotList: sSnapshotList,
  } = storeToRefs(store);

  const graphicInfoList = sGraphicInfoList as Ref<GraphicInfo[]>;
  const initGraphicInfoList = sInitGraphicInfoList as Ref<GraphicInfo[]>;
  const snapshotList = sSnapshotList as Ref<GraphicInfo[][]>;

  const maxLandNumber = ref(5);
  let preSnapshot: GraphicInfo[] = [];
  const activeId = ref<number>();

  const floatNumberFormat = (n: number | undefined) =>
    n === undefined ? 0 : n === 0 ? 0 : n.toFixed(2);

  const findItemById = (id: number) => {
    const item = graphicInfoList.value.find((i) => i.id === id);
    return item;
  };

  const copyGraphicInfoList = (list: GraphicInfo[]) =>
    list.map((g) => ({
      ...g,
      path: clonePath(g.path),
    }));

  const getActiveItem = () => {
    if (activeId.value !== undefined) {
      return findItemById(activeId.value);
    } else {
      return undefined;
    }
  };

  const handleAddSnapshot = () => {
    nextTick(() => {
      snapshotList.value.push(preSnapshot);
      preSnapshot = copyGraphicInfoList(graphicInfoList.value);
    });
  };

  // 撤销
  const handleUndo = () => {
    if (!snapshotList.value.length) return;

    graphicInfoList.value = snapshotList.value.splice(
      snapshotList.value.length - 1,
      1
    )[0];

    if (!snapshotList.value.length) {
      preSnapshot = copyGraphicInfoList(initGraphicInfoList.value);
    } else {
      preSnapshot = copyGraphicInfoList(graphicInfoList.value);
    }
  };

  // 重新绘制
  const handleReset = () => {
    graphicInfoList.value = [];
    preSnapshot = [];
    snapshotList.value = [];
  };

  // 开始绘制
  const handleStart = () => {
    store.drawModel = DRAW_MODEL.create;
    graphicInfoList.value = [];
    store.isStarted = true;
  };

  // 地图点击事件
  // 创建点
  const handleMapClick = (point: LatLngTuple) => {
    if (!store.isStarted) return;

    if (
      graphicInfoList.value.length === maxLandNumber.value &&
      graphicInfoList.value.every((i) => i.isClosed)
    ) {
      Toast(`单次最多可圈${maxLandNumber.value}个地块哦`);
      return;
    }

    const lastIndex = graphicInfoList.value.length - 1;
    if (
      !graphicInfoList.value.length ||
      graphicInfoList.value[lastIndex].isClosed
    ) {
      graphicInfoList.value.push({
        path: [point],
        isClosed: false,
        id: lastIndex + 1,
        area: 0,
        length: 0,
      });
    } else {
      graphicInfoList.value[lastIndex].path.push(point);
    }

    graphicInfoList.value = [...graphicInfoList.value];

    handleAddSnapshot();
  };

  // 图形闭合事件
  const handlePolygonClosed = (id: number) => {
    activeId.value = id;
    const item = findItemById(id)!;
    item.isClosed = true;

    graphicInfoList.value = [...graphicInfoList.value];

    handleAddSnapshot();
  };

  // 图形路径变化
  const handlePolygonPathChange = (id: number, path: LatLngTuple[]) => {
    activeId.value = id;
    findItemById(id)!.path = path;

    handleAddSnapshot();
  };

  // 图形统计数据变化
  const handleGraphicInfoChange = (
    id: number,
    length: number,
    area: number
  ) => {
    activeId.value = id;
    const item = findItemById(id)!;

    item.length = length;
    item.area = area;
  };

  const perimeter = computed(() => {
    const activeItem = getActiveItem();
    return activeItem === undefined ? 0 : floatNumberFormat(activeItem.length);
  });
  const areaSquareMeter = computed(() => {
    const activeItem = getActiveItem();
    return activeItem === undefined ? 0 : floatNumberFormat(activeItem.area);
  });
  const areaMu = computed(() => {
    const activeItem = getActiveItem();
    const mu = floatNumberFormat(squareMeterToMu(+areaSquareMeter.value!));
    const quequanAreaMu = activeItem?.extInfo?.quequanAreaMu as number;

    // 有确权数据面积优先显示确权亩
    return activeItem === undefined
      ? mu
      : quequanAreaMu
      ? floatNumberFormat(quequanAreaMu)
      : mu;
  });

  watchEffect(() => {
    // 监听编辑时初始化图形列表的数据
    if (initGraphicInfoList.value && initGraphicInfoList.value.length) {
      preSnapshot = copyGraphicInfoList(initGraphicInfoList.value);
      graphicInfoList.value = copyGraphicInfoList(initGraphicInfoList.value);
      activeId.value =
        initGraphicInfoList.value[initGraphicInfoList.value.length - 1].id;
    }
  });

  // 编辑单个
  useOnEditorSingleLand((data) => {
    initGraphicInfoList.value = data;

    // 没有闭合代表没有经纬度
    // 则进入绘制逻辑
    if (data[0].isClosed) {
      store.drawModel = DRAW_MODEL.update;
      eventHub.$emit("rerender-map");
    } else {
      store.drawModel = DRAW_MODEL.create;
      maxLandNumber.value = 1;
      store.isStarted = true;
    }
  });

  // 保存
  const handleSave = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const list: any[] = [];
    const len = graphicInfoList.value.length;

    const { setProgress, clear } = loading("正在保存中...", 0);

    for (let i = 0; i < len; i++) {
      const g = graphicInfoList.value[i];
      const [err, screenshot] = await getPolygonImgBase64([g]);

      // 截图为空表明用户加载底图失败
      if (err !== null) {
        clear();
        Toast(`保存失败,请检查您的网络!`);
        return;
      }

      if (process.env.NODE_ENV === "development") {
        const img = document.createElement("img");
        img.src = screenshot as string;
        document.body.appendChild(img);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const item: any = {
        area: squareMeterToMu(getAreaByCoords(g.path)),
        length: getLengthByCoords(g.path, true),
        center: JSON.stringify(
          latLngToLngLat(calculateCenter(clonePath(g.path))!)
        ),
        path: JSON.stringify(
          toClosedPath(
            removeDuplicateItem(latLngListToLngLatList(clonePath(g.path)))
          )
        ),
        screenshot,
      };

      if (process.env.NODE_ENV === "development") {
        console.log("area", squareMeterToMu(g.area!));
      }

      if (g.extInfo?.id) {
        item.id = g.extInfo.id;
      }

      list.push(item);

      // 因为保存地块分为几步，第一步为截图，第二步为 APP 上传图片保存
      // 所以截图的进度固定为 0 - 90 这一段，后面为 APP 上传图片保存的进度
      const progress = ((i + 1) / len) * 90;
      setProgress(parseInt(progress + "", 10));
    }

    clear();

    JSCallAPP({
      method: "save_land",
      data: list,
    });
  };

  return {
    handleSave,
    handleStart,
    handleMapClick,
    handlePolygonPathChange,
    handlePolygonClosed,
    handleGraphicInfoChange,
    handleUndo,
    handleReset,
    perimeter,
    areaSquareMeter,
    areaMu,
  };
};
